import merge from 'deepmerge'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { useCallback } from 'react'

import { type PAGE_TYPE } from '@/constants/pageTypes'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { useLocale } from '@/hooks/useLocale'
import { buildDataLayer, clearDataLayer, pushToDataLayer, trackPageView, usePage } from '@/lib/ga'
import { useTrackOnceOnLoad } from '@/lib/ga/hooks/useTrackOnceOnLoad'

export type UseTrackingProps = {
  /**
   * Additional data to be added to the dataLayer
   */
  data?: Record<string, unknown>
  /**
   * Page type for Google Analytics.
   * Refer to https://wiki.es.ecg.tools/pages/viewpage.action?pageId=98439770
   */
  pageType: PAGE_TYPE
  /**
   * Determines whether or not we should clear the dataLayer, we may not want to in some instances
   * when manually calling the `reportPageview` function (ex. when changing data related to a filter)
   */
  shouldClearDataLayer?: boolean
  /**
   * Determines whether or not we should clear the dataLayer. We may not want to clear the
   * dataLayer in some instances. For example, when manually calling the `reportPageview` function (ex. when changing data related to a filter).
   */
  shouldTrackInitial?: boolean
}

export const useTracking = ({
  data = {},
  pageType,
  shouldClearDataLayer = true,
  shouldTrackInitial = true,
}: UseTrackingProps) => {
  const { location } = useGetLocation()
  const { apiLocale } = useLocale()
  const { data: session } = useSession()
  const { asPath } = useRouter()
  const pageData = usePage({ pageType })

  /**
   * reportPageview exists to manually trigger a pageview event in the case content changes
   * but the page does not.
   *
   * Possible examples include filter changes in the SRP as we now perform a refetch instead
   * of a hard reload of the page. This will allow us to mimic the behaviour in BOX.
   * @param additionalData any additional data you want to send with the pageview event
   */
  const reportPageview = async (
    additionalData: Record<string, unknown> = {},
    shouldClearDataLayer = true
  ) => {
    if (shouldClearDataLayer) {
      clearDataLayer()
    }
    pushToDataLayer(
      await buildDataLayer({
        session,
        additionalData: merge(pageData, additionalData),
        apiLocale,
        location,
      })
    )
    trackPageView(asPath)
  }

  const triggerTracking = useCallback(async () => {
    pushToDataLayer(
      await buildDataLayer({ session, additionalData: merge(pageData, data), apiLocale, location })
    )
    trackPageView(asPath)
  }, [session, pageData, data, apiLocale, location, asPath])

  useTrackOnceOnLoad({
    triggerTracking: triggerTracking,
    shouldTrack: shouldTrackInitial,
    shouldClearDataLayer,
  })

  return { reportPageview }
}
